import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { IContextProvider } from "@uxp";
import React, { CSSProperties, ReactNode } from "react";
import { ContextDatasection, PillConfiguration } from "uxp/components";

export interface IUXPDefaultProps {
    uxpContext: IContextProvider,
}

export type FilteredOrigins = ('collection' | 'lucy' | 'query')[]
export interface IDataSourceBrowserProps {
    source: IDataSource | null
    onSelect: (source: IDataSource) => void,
    showCloseButton?: boolean
    onClose?: () => void,
    history?: boolean,
    origins?: FilteredOrigins,
    configuredSources?: IDataSource[]
    query: IQuery
}
export interface IPreviewProps {
    context: IQueryBuilderContext;
    schema: ISourceSchema;
    data: any;
}
export interface IQueryEditorProps {
    query: IQuery,
    onChange: (query: IQuery) => void,
    onUpdateSavedQuery: (queryId: string) => void
}

export interface SourceSelectorInputProps {
    source: IDataSource | IStep | null,
    onChange: (source: IDataSource | IStep) => void,
    availableSources: (IStep | IDataSource)[]
    history?: boolean,
    query?: IQuery
}

export interface MultipleSourcesSelectorInputProps {
    sources: (IDataSource | IStep)[],
    onChange: (sources: (IDataSource | IStep)[]) => void,
    availableSources: (IStep | IDataSource)[],
    query?: IQuery
}

export interface StepConfiguratorProps {
    query: IQuery,
    onChange: (query: IQuery) => void,
    selectedStepId: string,
    context: IQueryBuilderContext,
}

export type ICustomInputRendererProps = (value: string, onChange: (value: string) => void) => React.ReactNode
export interface IQueryBuilderContext extends IUXPDefaultProps {
    renderCustomInput?: ICustomInputRendererProps,
    // pass this to prevent users from selecting the same query as a datasource when editing 
    editingQueryId?: string,
    externalContextDataSections?: ContextDatasection[],
    externalPillConfiguration?: PillConfiguration,
    canSelectSavedQuerries?: boolean
}

export enum StepType {
    Filter = 'filter',
    Aggregate = 'aggregate',
    JOIN = 'join',
    CustomTransform = 'custom-transform',
    CustomExpand = 'custom-expand',
    Limit = 'limit',
    Select = 'select',
    Sort = 'sort',
    Union = 'union',
    History = 'history',
}

export interface IStepBaseProps {
    id: string
    name: string
    schema: ISourceSchema,
    ui?: any
    params?: { [source: string]: Record<string, string> }
}

export interface IFilterStep extends IStepBaseProps {
    type: StepType.Filter
    source: string
    filter: any
    ignoreFiltersIfValueIsEmpty?: boolean
}

export enum IAggregationType {
    Sum = 'sum',
    Average = 'average',
    First = 'first',
    Last = 'last',
    Min = 'min',
    Max = 'max',
    CSV = 'csv',
    Count = 'count'
}

export interface IAggregateField {
    field: string;
    type: IAggregationType;
    name?: string | null;
}
export interface IAggregation {
    fields: string[];
    aggregates: IAggregateField[];
}
export interface IAggregateStep extends IStepBaseProps {
    type: StepType.Aggregate
    source: string
    aggregation: IAggregation
}

export interface IConditionProps {
    source1: string, source2: string
}
export interface IJoinField {
    field: string;
    newField: string;
}
export interface IJoinStep extends IStepBaseProps {
    type: StepType.JOIN;
    source1: string;
    source2: string;
    on: IConditionProps[];
    optional: boolean;
    source2Fields: IJoinField[];
}

export interface ICustomTransformStep extends IStepBaseProps {
    type: StepType.CustomTransform;
    source: string;
    description: string;
    code: string;
    added: string[];
    removed: string[];
}

export interface ICustomExpandStep extends IStepBaseProps {
    type: StepType.CustomExpand;
    source: string;
    description: string;
    code: string;
}

export interface ILimitStep extends IStepBaseProps {
    type: StepType.Limit;
    source: string;
    rows: number;
}
export enum IHistoryBucket {
    OneMin = '1min',
    FiveMin = '5min',
    FifteenMin = '15min',
    ThirtyMin = '30min',
    Hour = 'hour',
    Day = 'day',
    Week = 'week',
    Month = 'month',
    Year = 'year'
}
export interface IHistoryStep extends IStepBaseProps {
    type: StepType.History;
    source: string;
    field: string;
    aggregation: IAggregationType;
    historicalDataSourceId: string;
    historicalDataSourceField: string;
    starts: string;
    ends: string;
    bucket: IHistoryBucket;
}

export interface ISelectStep extends IStepBaseProps {
    type: StepType.Select;
    source: string;
    include: string[];
}

export interface ISortAttribute {
    field: string;
    descending: boolean;
}
export interface ISortStep extends IStepBaseProps {
    type: StepType.Select;
    source: string;
    attributes: ISortAttribute[];
}

export interface IUnionStep extends IStepBaseProps {
    type: StepType.Select;
    sources: string[];
}

export type IStep = IFilterStep | IAggregateStep | IJoinStep | ICustomTransformStep | ICustomExpandStep | ILimitStep | ISelectStep | ISortStep | IUnionStep | IHistoryStep;


export enum DataSourceType {
    SQL = 'sqltable',
    COLLECTION = 'collection',
    WORKFLOW = 'lucy',
    JSON = 'json',
    DX = 'dx',
    Query = 'query',
}

export interface IDSField {
    name: string,
    type?: string,
    description?: string,
    example?: string,
    error?: string,
    historicalData?: {
        measurementField: string,
        template: string
    }
}

export interface IDataSourceMetadata {
    model?: string,
    collection?: string,
    action?: string,
    table?: string,
    queryName?: string,
    queryId?: string
}
export interface IDataSourceBaseProps {
    source: string
    id: string
    name: string,
    description: string,
    schema: ISourceSchema
    fields: IDSField[],
    history?: boolean
    origin?: string
    params?: Param[]
}

export interface ISQLDataSource extends IDataSourceBaseProps {
    type: DataSourceType.SQL;
    source: string;
}
export interface IDXDataSource extends IDataSourceBaseProps {
    type: DataSourceType.DX;
    source: string;
}

export interface ICollectionDataSource extends IDataSourceBaseProps {
    type: DataSourceType.COLLECTION;
    model: string;
    collection: string;
}

export interface IJSONDataSource extends IDataSourceBaseProps {
    type: DataSourceType.JSON;
    data: any[] | { [key: string]: any }
}

export interface IWorkflowDataSource extends IDataSourceBaseProps {
    type: DataSourceType.WORKFLOW;
    model: string;
    action: string;
}

export interface IQueryDataSource extends IDataSourceBaseProps {
    type: DataSourceType.Query,
    queryName: string,
    queryId: string
}

export type IDataSource = ISQLDataSource | ICollectionDataSource | IJSONDataSource | IWorkflowDataSource | IDXDataSource | IQueryDataSource;

export interface Param {
    id: string,
    name: string,
    type: 'string' | 'number' | 'boolean',
    defaultValue?: string
    value?: string
}
export interface IQuery {
    sources: IDataSource[],
    steps: IStep[],
    params?: Param[]
    // this will be used when select a saved query 
    sourceOnly?: boolean,
    configuredParams?:Record<string, any>
}

export interface IDataSourceBrowserModalProps extends IDataSourceBrowserProps {
    show: boolean,
    onClose: () => void
}

export interface ICustomIconButton {
    icon: IconProp
    onClick?: () => void
}

export interface IStepSelectorProps {
    onSelect: (step: StepType) => void
}

interface IDefaultProps extends IStepBaseProps { type: StepType }
export type IRegisteredStep = {
    type: StepType
    name: string,
    description: string,
    icon?: string,
    iconStyles?: CSSProperties,
    render: (context: IQueryBuilderContext, query: IQuery, onChange: (query: IQuery) => void, selectedStepId: string) => ReactNode;
    validate: (query: IQuery, step: IStep) => { isValid: boolean, errors: Record<string, string> }
    onSelectStep: (basicProps: IDefaultProps, previousSource: string) => IStep
};

export interface IStepValidationResponse {
    isValid: boolean,
    errors: Record<string, string>
}

export type IFieldType = 'string' | 'number' | 'json' | 'date' | 'boolean' | 'other';
export interface IFieldDefinition {
    name: string;
    type: IFieldType;
}
export type ISourceSchema = IFieldDefinition[];


// data source broweser 
export interface IFilters {
    type?: DataSourceType | '__all__',
    origin?: string
}

export interface ISourceOrigins {
    [name: string]: {
        types: DataSourceType[],
        sources: string[]
    }
}